import {
    Box,
    Stack,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import TileButton from "../../components/buttons/tileButton"
import CheckMarkList from "../../components/dataDisplay/lsits/checkMarkList"
import AppStepper from "../../components/dataDisplay/stepper/appStepper"
import {
    EVChargerIcon,
    HeatPumpIcon,
    SolarPanelIcon,
} from "../../components/icons/icons"
import LctHoneyCombMask from "../../components/images/lctHoneyCombMask"
import DisplayIf from "../../components/wrappers/displayIf"
import DisplayIfElse from "../../components/wrappers/displayIfElse"
import { Datasets } from "../../constants/enums"
import { SUPPORT_REFER_HELP_DESK } from "../../errors/errorMessages"
import { useScreenSize } from "../../hooks/useScreenSize"
import {
    EVAC_PUBLISH_DEVICE,
    EVDC_PUBLISH_DEVICE,
    HP_PUBLISH_DEVICE,
    TTR_PUBLISH_DEVICE,
} from "../../router/routes"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import { snackbar } from "../../utils/general/generalUtils"

const GUIDANCE_STEPS = [
    "You fill out this form to publish a new device",
    "ENA evaluators review device and assign a status",
    "Device is published on ENA asset register",
    "You can log into Connect Direct to view and update your device",
]

const PUBLISH_DEVICE_TILES = [
    {
        title: "Generation Device",
        value: Datasets.TTR,
        icon: SolarPanelIcon,
    },
    {
        title: "Heat Pump",
        value: Datasets.HP,
        icon: HeatPumpIcon,
    },
    {
        title: "EV Charger (AC)",
        value: Datasets.EVAC,
        icon: EVChargerIcon,
    },
    {
        title: "EV Charger (DC)",
        value: Datasets.EVDC,
        icon: EVChargerIcon,
    },
]

/**
 * Entry point for manufacturers publishing new devices on connect direct.
 * Directs user to the required form based on the LCT selected
 */
const SelectDeviceToPublish = (): ReactElement => {
    const theme = useTheme()
    const { isTabletOrMob, isLg } = useScreenSize()
    const navigate = useNavigate()

    const navigateToForm = (tileValue: string): void => {
        if (tileValue === Datasets.TTR) {
            navigate(TTR_PUBLISH_DEVICE)
        } else if (tileValue === Datasets.HP) {
            navigate(HP_PUBLISH_DEVICE)
        } else if (tileValue === Datasets.EVAC) {
            // navigate to EVAC form
            navigate(EVAC_PUBLISH_DEVICE)
        } else if (tileValue === Datasets.EVDC) {
            // navigate to EVDC form
            navigate(EVDC_PUBLISH_DEVICE)
        } else {
            snackbar(
                `Something went wrong! Please ${SUPPORT_REFER_HELP_DESK}`,
                "error"
            )
        }
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexGrow: 1,
                height: "100%",
                padding: 0,
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 2, mg: 6 }}
                width="100%"
            >
                <DisplayIf condition={!isTabletOrMob}>
                    <LctHoneyCombMask maxWidth="45%" />
                </DisplayIf>

                <Stack
                    direction="column"
                    spacing={GRID_SPACING}
                    width="100%"
                    padding={2}
                >
                    <Box>
                        <Typography
                            variant={isTabletOrMob ? "h4" : "h3"}
                            color="text.primary"
                            fontWeight={500}
                        >
                            Register a New Device with Us
                        </Typography>
                        <Typography
                            variant="h6"
                            gutterBottom
                            color="text.secondary"
                            fontWeight="light"
                        >
                            Let&rsquo;s get started! Firstly, tell us which type
                            of device you would like to publish
                        </Typography>
                    </Box>
                    <ToggleButtonGroup
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr",
                                lg: "1fr 1fr",
                            },
                            gridGap: {
                                xs: theme.spacing(1.5),
                                md: theme.spacing(2),
                                lg: theme.spacing(3),
                            },
                            paddingBottom: theme.spacing(3),
                        }}
                        exclusive
                    >
                        {PUBLISH_DEVICE_TILES.map((tile) => (
                            <TileButton
                                key={tile.value}
                                title={tile.title}
                                value={tile.value}
                                Icon={tile.icon}
                                onClick={() => navigateToForm(tile.value)}
                                selected={false}
                            />
                        ))}
                    </ToggleButtonGroup>
                    <Box>
                        <Typography
                            variant="body1"
                            fontWeight={500}
                            color="text.primary"
                        >
                            How does this process work?
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            The process of publishing a device on the ENA asset
                            register is simple and easy to follow. It means your
                            device will be visible to installers and other
                            people using Connect Direct.
                        </Typography>

                        <Typography
                            variant="body1"
                            color="text.secondary"
                            marginTop={1}
                            marginBottom={2}
                        >
                            Here&rsquo;s a quick overview:
                        </Typography>

                        <DisplayIfElse
                            condition={isLg}
                            displayIfTrue={
                                <AppStepper
                                    stepLabels={GUIDANCE_STEPS}
                                    currentStepIndex={GUIDANCE_STEPS.length}
                                />
                            }
                            displayIfFalse={
                                <CheckMarkList items={GUIDANCE_STEPS} />
                            }
                        />
                    </Box>
                </Stack>
            </Stack>
        </Box>
    )
}

export default SelectDeviceToPublish
